import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../lib/auth';
import Loading from '../Loading';
import { errorBox, inputClass, noticeBox } from '../AuthLayout';
import { PLANS, TEAM_MAX_SEATS, TEAM_MIN_SEATS, getPlan, money, periodTotal, yearlySavingPercent } from '../../config/plans';
import { IntervalToggle, billingNote, priceLine } from './Pricing';
import { Check, SALES_HOME, SIGNUP_BASE, usePageTitle } from './shared';
import GoogleButton, { OrDivider } from '../GoogleButton';
import { startCheckout } from '../../utils/billing';
import { usePricing } from '../../lib/pricing';
import { BRAND } from '../../config/brand';

const STORE_KEY = 'tapzo-signup-choice';

const readStore = () => {
  try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}'); } catch { return {}; }
};
const writeStore = (v) => {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private mode */ }
};

const clampSeats = (n) => Math.min(TEAM_MAX_SEATS, Math.max(TEAM_MIN_SEATS, Math.floor(Number(n) || TEAM_MIN_SEATS)));

const button = 'w-full h-12 rounded-xl bg-blue text-white font-semibold text-[16px] hover:bg-blue-dark transition disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue';
const linkBtn = 'text-[15px] font-semibold text-blue hover:underline';

/**
 * /start?plan=free|pro|teams&interval=month|year[&resume=1]
 * Plan choice -> create account (or sign in) -> set up the account -> Stripe Checkout for paid plans.
 */
export default function SalesSignup() {
  usePageTitle(`Create your ${BRAND.name} account`);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, user, loaded, memberships, refreshProfile } = useAuth();
  const pricing = usePricing();

  const stored = useMemo(readStore, []);
  const [planId, setPlanId] = useState(() => (PLANS.some((p) => p.id === params.get('plan')) ? params.get('plan') : stored.plan || 'free'));
  const [interval, setBilling] = useState(() => (['month', 'year'].includes(params.get('interval')) ? params.get('interval') : stored.interval || 'year'));
  const [seats, setSeats] = useState(() => clampSeats(params.get('seats') || stored.seats));
  const [seatsText, setSeatsText] = useState(String(clampSeats(params.get('seats') || stored.seats)));
  const [company, setCompany] = useState(stored.company || '');
  const [name, setName] = useState(stored.name || '');
  const resume = params.get('resume') === '1';
  const canceled = params.get('canceled') === '1';

  // The saved minimum seats can arrive after the first render
  useEffect(() => {
    setSeats((s) => clampSeats(s));
    setSeatsText((t) => String(clampSeats(t)));
  }, [pricing]);

  const [step, setStep] = useState(resume ? 'finish' : 'plan'); // plan | account | email | finish
  const plan = getPlan(planId);
  const paid = plan.id !== 'free';

  const choice = useMemo(() => ({ plan: planId, interval, seats, company: company.trim(), name: name.trim() }), [planId, interval, seats, company, name]);

  // Keep the URL and sessionStorage in step with the choice, so a refresh or the email link resumes it
  useEffect(() => {
    writeStore(choice);
    const next = new URLSearchParams(params);
    next.set('plan', planId);
    next.set('interval', interval);
    if (planId === 'teams') next.set('seats', String(seats)); else next.delete('seats');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [choice]); // eslint-disable-line react-hooks/exhaustive-deps

  const resumePath = useCallback((extra = {}) => {
    const q = new URLSearchParams({ plan: planId, interval, ...(planId === 'teams' ? { seats: String(seats) } : {}), ...extra });
    return `${SIGNUP_BASE}?${q.toString()}`;
  }, [planId, interval, seats]);

  const choosePlan = (e) => {
    e.preventDefault();
    if (planId === 'teams') {
      if (!company.trim()) return;
      const n = clampSeats(seatsText);
      setSeats(n);
      setSeatsText(String(n));
    }
    setStep(session ? 'finish' : 'account');
  };

  if (session === undefined) return <Loading />;

  return (
    <div className="min-h-screen bg-surface text-ink font-sans lg:grid lg:grid-cols-[minmax(340px,440px)_1fr]">
      <Summary plan={plan} interval={interval} seats={seats} step={step} />
      <main className="px-4 sm:px-8 py-8 lg:py-14 flex justify-center">
        <div className="w-full max-w-[520px]">
          {canceled && step === 'plan' && (
            <div className={`${noticeBox} mb-6`}>Checkout was canceled and you haven’t been charged. You can pick up where you left off.</div>
          )}
          {step === 'plan' && (
            <PlanStep {...{ planId, setPlanId, interval, setBilling, seats, setSeats, seatsText, setSeatsText, company, setCompany, name, setName, onSubmit: choosePlan, user }} />
          )}
          {step === 'account' && (
            <AccountStep plan={plan} name={name} resumePath={resumePath} onBack={() => setStep('plan')}
              onSignedIn={() => setStep('finish')} onCheckEmail={() => setStep('email')} />
          )}
          {step === 'email' && <CheckEmail resumePath={resumePath} />}
          {step === 'finish' && (
            !session ? <SignInToContinue resumePath={resumePath} />
              : !loaded ? <Working text="Loading your account..." />
                : <Finish choice={choice} plan={plan} paid={paid} user={user} memberships={memberships}
                    refreshProfile={refreshProfile} navigate={navigate} resumePath={resumePath} onBack={() => setStep('plan')} />
          )}
        </div>
      </main>
    </div>
  );
}

/* ---------------- Left panel: what you're getting ---------------- */

function Summary({ plan, interval, seats, step }) {
  const { amount, unit } = priceLine(plan, interval);
  const total = periodTotal(plan, interval, seats);
  const steps = plan.id === 'free' ? ['Choose a plan', 'Create your account', 'Make your card'] : ['Choose a plan', 'Create your account', 'Start your free trial'];
  const current = step === 'plan' ? 0 : step === 'finish' ? 2 : 1;
  return (
    <aside className="bg-navy text-white px-4 sm:px-8 py-6 lg:py-12 lg:min-h-screen">
      <Link to={SALES_HOME} aria-label={`Back to ${BRAND.name}`}><img src={BRAND.logo.lockupOnDark} alt={BRAND.name} className="h-8 w-auto" /></Link>
      <div className="mt-6 lg:mt-14">
        <p className="text-[15px] text-[#9FB3CC]">Your plan</p>
        <p className="mt-1 font-display font-extrabold text-[34px] leading-[38px] tracking-[-0.02em]">{BRAND.name} {plan.name}</p>
        <p className="mt-2 flex items-baseline gap-2">
          <span className="font-display font-bold text-[24px]">{amount}</span>
          <span className="text-[15px] text-[#C9D6E6]">{unit}</span>
        </p>
        {plan.id !== 'free' && (
          <p className="mt-1 text-[14px] text-[#9FB3CC]">
            {plan.perSeat ? `${seats} people · ` : ''}{money(total)} {interval === 'year' ? 'per year' : 'per month'}
          </p>
        )}
        <ul className="mt-6 space-y-2.5 hidden sm:block">
          {plan.features.map((f) => {
            const text = typeof f === 'string' ? f : f.text;
            const soon = typeof f !== 'string' && f.soon;
            return <li key={text} className="flex gap-3 text-[15px] leading-[22px] text-[#E8EEFF]"><Check className="text-coral" /><span>{text}{soon && <span className="text-[#9FB3CC]"> (coming soon)</span>}</span></li>;
          })}
        </ul>
      </div>
      <ol className="mt-8 lg:mt-14 flex lg:flex-col gap-x-5 gap-y-3 flex-wrap text-[14px]" aria-label="Signup steps">
        {steps.map((s, i) => (
          <li key={s} className={`flex items-center gap-2.5 ${i === current ? 'text-white font-semibold' : i < current ? 'text-[#C9D6E6]' : 'text-[#7F8FA3]'}`}
            aria-current={i === current ? 'step' : undefined}>
            <span className={`w-6 h-6 rounded-full text-[12px] font-bold flex items-center justify-center ${i === current ? 'bg-blue text-white' : i < current ? 'bg-white/20 text-white' : 'border border-white/25'}`}>{i + 1}</span>
            <span className={i === current ? '' : 'hidden sm:inline'}>{s}</span>
          </li>
        ))}
      </ol>
    </aside>
  );
}

/* ---------------- Step 1: plan ---------------- */

function PlanStep({ planId, setPlanId, interval, setBilling, seats, setSeats, seatsText, setSeatsText, company, setCompany, name, setName, onSubmit, user }) {
  const saving = yearlySavingPercent(getPlan(planId)) || Math.max(...PLANS.map(yearlySavingPercent));
  const commitSeats = (v) => { const n = clampSeats(v); setSeats(n); setSeatsText(String(n)); };
  return (
    <form onSubmit={onSubmit} className="space-y-7">
      <div>
        <h1 className="font-display font-extrabold text-[32px] leading-[36px] sm:text-[38px] sm:leading-[42px] tracking-[-0.02em]">Choose your plan</h1>
        <p className="mt-2 text-[16px] text-ink-muted">Not sure yet? Start with Free and upgrade when you want the leads.</p>
      </div>

      <fieldset>
        <legend className="sr-only">Plan</legend>
        <div className="space-y-2.5">
          {PLANS.map((p) => {
            const on = p.id === planId;
            const { amount, unit } = priceLine(p, interval);
            return (
              <label key={p.id} className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer bg-white transition ${on ? 'border-blue' : 'border-line hover:border-line-strong'}`}>
                <input type="radio" name="plan" value={p.id} checked={on} onChange={() => setPlanId(p.id)} className="sr-only peer" />
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 peer-focus-visible:ring-2 peer-focus-visible:ring-blue peer-focus-visible:ring-offset-2 ${on ? 'border-blue' : 'border-line-strong'}`} aria-hidden="true">
                  {on && <span className="w-2.5 h-2.5 rounded-full bg-blue" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-display font-bold text-[19px]">{p.name}</span>
                  <span className="block text-[14px] text-ink-muted">{p.tagline}</span>
                </span>
                <span className="text-right shrink-0">
                  <span className="block font-display font-bold text-[19px]">{amount}</span>
                  <span className="block text-[12px] text-ink-muted max-w-[90px] leading-4">{unit}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {planId !== 'free' && (
        <div className="flex flex-wrap items-center gap-3">
          <IntervalToggle interval={interval} onChange={setBilling} saving={saving} />
          <p className="text-[14px] text-ink-muted">{billingNote(getPlan(planId), interval)}</p>
        </div>
      )}

      <div className="space-y-4">
        <label className="block">
          <span className="block text-[15px] font-medium mb-1.5">Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} autoComplete="name" maxLength={120} required />
        </label>
        {planId === 'teams' && (
          <>
            <label className="block">
              <span className="block text-[15px] font-medium mb-1.5">Company name</span>
              <input value={company} onChange={(e) => setCompany(e.target.value)} className={inputClass} autoComplete="organization" maxLength={120} required />
            </label>
            <div>
              <label htmlFor="seats" className="block text-[15px] font-medium mb-1.5">How many people need a card?</label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => commitSeats(seats - 1)} disabled={seats <= TEAM_MIN_SEATS} aria-label="One fewer person"
                  className="w-11 h-11 rounded-lg border border-line-strong bg-white text-[20px] font-semibold disabled:opacity-40">−</button>
                <input id="seats" inputMode="numeric" value={seatsText} onChange={(e) => setSeatsText(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  onBlur={(e) => commitSeats(e.target.value)} className={`${inputClass} !w-20 text-center`} aria-describedby="seats-hint" />
                <button type="button" onClick={() => commitSeats(seats + 1)} disabled={seats >= TEAM_MAX_SEATS} aria-label="One more person"
                  className="w-11 h-11 rounded-lg border border-line-strong bg-white text-[20px] font-semibold disabled:opacity-40">+</button>
              </div>
              <p id="seats-hint" className="mt-1.5 text-[14px] text-ink-muted">Minimum {TEAM_MIN_SEATS}. One seat is one person with a card.</p>
            </div>
          </>
        )}
      </div>

      <button type="submit" className={button}>
        {user ? (planId === 'free' ? 'Set up my card' : 'Start free trial') : 'Continue'}
      </button>
      {user ? (
        <p className="text-[14px] text-ink-muted text-center">Signed in as {user.email}</p>
      ) : (
        <p className="text-[15px] text-ink-muted text-center">Already have an account? <SignInLink /></p>
      )}
    </form>
  );
}

function SignInLink({ children = 'Sign in' }) {
  const [params] = useSearchParams();
  const q = new URLSearchParams(params);
  q.set('resume', '1');
  q.delete('canceled');
  const next = `${SIGNUP_BASE}?${q.toString()}`;
  return <Link to={`/login?next=${encodeURIComponent(next)}`} className={linkBtn}>{children}</Link>;
}

/* ---------------- Step 2: account ---------------- */

function AccountStep({ plan, resumePath, onBack, onSignedIn, onCheckEmail }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Use at least 8 characters for your password.');
    if (password !== confirm) return setError('The passwords do not match.');
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: `${window.location.origin}${resumePath({ resume: '1' })}` },
    });
    setLoading(false);
    if (error) return setError(error.message);
    // Supabase answers an existing email with a user without identities and sends nothing.
    if (data.user && data.user.identities?.length === 0) {
      return setError(`There is already an account for ${email.trim()}. Sign in below instead (or choose “Forgot password?” there).`);
    }
    if (data.session) onSignedIn();
    else onCheckEmail(email.trim());
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <div>
        <button type="button" onClick={onBack} className={linkBtn}>Change plan</button>
        <h1 className="mt-3 font-display font-extrabold text-[32px] leading-[36px] sm:text-[38px] sm:leading-[42px] tracking-[-0.02em]">Create your account</h1>
        <p className="mt-2 text-[16px] text-ink-muted">
          {plan.id === 'free' ? 'Then you’ll make your card.' : 'Then you’ll pay securely with Stripe.'}
        </p>
      </div>
      <div>
        <GoogleButton redirectPath={resumePath({ resume: '1' })} label="Sign up with Google" disabled={loading} />
        <OrDivider />
      </div>
      <div className="space-y-4">
        <label className="block">
          <span className="block text-[15px] font-medium mb-1.5">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} autoComplete="email" required disabled={loading} />
        </label>
        <label className="block">
          <span className="block text-[15px] font-medium mb-1.5">Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} autoComplete="new-password" required disabled={loading} aria-describedby="pw-hint" />
        </label>
        <p id="pw-hint" className="-mt-2.5 text-[14px] text-ink-muted">At least 8 characters.</p>
        <label className="block">
          <span className="block text-[15px] font-medium mb-1.5">Confirm password</span>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputClass} autoComplete="new-password" required disabled={loading} />
        </label>
      </div>
      {error && <div className={errorBox} role="alert">{error}</div>}
      <button type="submit" disabled={loading} className={button}>{loading ? 'Creating account...' : 'Create account'}</button>
      <p className="text-[15px] text-ink-muted text-center">Already have an account? <SignInLink /></p>
    </form>
  );
}

function CheckEmail() {
  return (
    <div className="space-y-5">
      <h1 className="font-display font-extrabold text-[32px] leading-[36px] tracking-[-0.02em]">Check your email</h1>
      <p className="text-[17px] leading-[27px] text-ink-muted">
        We sent you a link to confirm your email. Open it on this device and we’ll bring you straight back here to finish setting up.
      </p>
      <div className={noticeBox}>Your plan choice is saved. If the link signs you out, sign in and you’ll continue from this step.</div>
      <p className="text-[15px] text-ink-muted">Already confirmed? <SignInLink>Sign in to continue</SignInLink></p>
    </div>
  );
}

function SignInToContinue() {
  return (
    <div className="space-y-5">
      <h1 className="font-display font-extrabold text-[32px] leading-[36px] tracking-[-0.02em]">Sign in to continue</h1>
      <p className="text-[17px] leading-[27px] text-ink-muted">Your plan choice is saved. Sign in and we’ll finish setting up your account.</p>
      <GoogleButton redirectPath={`${window.location.pathname}${window.location.search}`} className="max-w-sm" />
      <SignInLink>Sign in with email</SignInLink>
    </div>
  );
}

function Working({ text }) {
  return (
    <div className="flex items-center gap-4 py-10" role="status">
      <span className="w-6 h-6 rounded-full border-[3px] border-blue-soft border-t-blue animate-spin" aria-hidden="true" />
      <p className="text-[17px]">{text}</p>
    </div>
  );
}

/* ---------------- Step 3: set up the account, then pay ---------------- */

function Finish({ choice, plan, paid, user, memberships, refreshProfile, navigate, resumePath, onBack }) {
  const [state, setState] = useState({ status: 'working', text: 'Setting up your account...' });
  const started = useRef(false);

  const run = useCallback(async () => {
    setState({ status: 'working', text: 'Setting up your account...' });
    try {
      const fallbackName = choice.name || user.email.split('@')[0];
      let orgId;
      if (plan.id === 'teams') {
        if (!choice.company) { onBack(); return; }
        const { data, error } = await supabase.rpc('create_company_organization', { p_name: choice.company });
        if (error) throw new Error(error.message);
        orgId = data;
      } else {
        const personal = memberships.find((m) => m.organizations?.kind === 'personal' && ['owner', 'admin'].includes(m.role));
        if (personal) orgId = personal.org_id;
        else if (plan.id === 'free' && memberships.length) orgId = null; // already belongs to a company; nothing to create
        else {
          const { data, error } = await supabase.rpc('create_personal_organization', { p_name: fallbackName });
          if (error?.hint === 'invite_pending') {
            // A company invited this email: the dashboard offers to join it instead.
            try { sessionStorage.removeItem('tapzo-signup-choice'); } catch { /* ignore */ }
            navigate('/', { replace: true });
            return;
          }
          if (error) throw new Error(error.message);
          orgId = data;
        }
      }
      await refreshProfile();

      if (!paid) {
        try { sessionStorage.removeItem('tapzo-signup-choice'); } catch { /* ignore */ }
        navigate('/', { replace: true });
        return;
      }

      setState({ status: 'working', text: 'Opening secure checkout...' });
      const url = await startCheckout({
        org_id: orgId,
        plan: plan.id,
        interval: choice.interval,
        seats: plan.id === 'teams' ? choice.seats : undefined,
        cancel_path: resumePath({ canceled: '1' }),
      });
      window.location.assign(url);
    } catch (err) {
      setState({ status: 'error', text: err.message, code: err.code });
    }
  }, [choice, plan, paid, user, memberships, refreshProfile, navigate, resumePath, onBack]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run();
  }, [run]);

  if (state.status === 'working') return <Working text={state.text} />;

  const notReady = state.code === 'payments_not_configured';
  return (
    <div className="space-y-5">
      <h1 className="font-display font-extrabold text-[32px] leading-[36px] tracking-[-0.02em]">
        {notReady ? 'Your account is ready' : 'Checkout didn’t start'}
      </h1>
      <div className={notReady ? noticeBox : errorBox} role="alert">{state.text}</div>
      {notReady && <p className="text-[16px] text-ink-muted">You can make your card now and upgrade once payments are on.</p>}
      <div className="flex flex-col sm:flex-row gap-3">
        {!notReady && <button type="button" onClick={run} className={button}>Try again</button>}
        <Link to="/" className="w-full h-12 rounded-xl border border-line-strong bg-white font-semibold flex items-center justify-center hover:bg-surface">Go to my card</Link>
      </div>
      <button type="button" onClick={onBack} className={linkBtn}>Change plan</button>
    </div>
  );
}
